import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import Button from '../../../reusable/Button/Button';
import Input from '../../../reusable/Input/Input';
import NumberInput from '../../../reusable/NumberInput/NumberInput';
import Select from '../../../reusable/Select/Select';
import Skeleton from '../../../reusable/Skeleton/Skeleton';
import { toast } from '../../../reusable/Toast/toastStore';
import { selectUser } from '../../../store/slices/authSlice';
import {
  useGetDailyLossAlertEmailQuery,
  useGetProfileDetailsQuery,
  useUpdateDailyLossAlertEmailMutation,
  useUpdateProfileDetailsMutation,
} from '../../../store/api/profileApi';
import {
  countryName,
  DEFAULT_COUNTRY_CODE,
  DEFAULT_DIAL_CODE,
  findCountry,
} from '../../../utilities/country';
import { t } from '../../../i18n/i18n';
import './Personal.scss';

/**
 * Same rules the checkout's Customer Information enforces (TradingAssessment) —
 * the two forms write the same trader record, so a name the checkout rejects
 * can't be saveable here.
 */
const nameRule = (label) =>
  Yup.string()
    .trim()
    .required(() => t('{{label}} is required', { label: t(label) }))
    .max(70, 'Must be at most 70 characters')
    .matches(/^[^0-9]*$/, 'Only letters, spaces, and hyphens allowed');

/**
 * What a contact number may be. Both ends are shared with the field itself
 * (its `maxLength`, and the sanitiser below), so the control can never hold a
 * value the schema would then reject — the rule and the input cannot drift.
 */
const PHONE_MIN = 6;
const PHONE_MAX = 15;

/**
 * Digits only, nothing else. Applied on every keystroke rather than only on
 * save: a space, a letter or a `+` never enters the value, so it cannot be
 * submitted, cannot be trimmed away silently by `toPatch` on the way out, and
 * needs no error message to explain itself.
 */
const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '').slice(0, PHONE_MAX);

const profileSchema = Yup.object({
  firstName: nameRule('First Name'),
  lastName: nameRule('Last Name'),
  phone: Yup.string()
    // Trimmed first, so a value that is only whitespace is caught by `required`
    // as the empty string it amounts to, rather than slipping past it and
    // failing further down on a less obvious rule.
    .trim()
    .required('Contact number is required')
    .matches(/^\d+$/, 'Contact number must contain only digits')
    .min(PHONE_MIN, ({ min }) => t('Contact number must be at least {{count}} digits', { count: min }))
    .max(PHONE_MAX, ({ max }) => t('Contact number must not exceed {{count}} digits', { count: max })),
  city: nameRule('City'),
  country: Yup.string().required('Country is required'),
  // Optional, unlike the checkout's: an older record can carry no date of
  // birth, so requiring one would block every other edit.
  dob: Yup.string(),
});

/** "Jetmir Kojqiqi" → "JK", which is what the avatar carries. */
const initials = (first, last) =>
  `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase() || 'U';

/**
 * The record as the endpoint sends it → the shape this form holds. `phoneCode`
 * stays apart from `phone` because NumberInput owns the dial-code picker, and
 * `country` arrives as the ISO short name while the select holds the code —
 * `findCountry` reads either.
 */
const toForm = (details, user) => {
  const record = details ?? {};
  const dial = String(record.country_code ?? '').replace(/\D/g, '');

  return {
    firstName: record.first_name || '',
    lastName: record.last_name || '',
    email: record.email || user?.email || '',
    phone: record.contact_number ? String(record.contact_number) : '',
    phoneCode: dial ? `+${dial}` : DEFAULT_DIAL_CODE,
    country: findCountry(record.country)?.code ?? DEFAULT_COUNTRY_CODE,
    city: record.city || '',
    dob: record.date_of_birth || '',
  };
};

/** The form's names → the record's, for the fields that go up unchanged. */
const PATCHABLE = {
  firstName: 'first_name',
  lastName: 'last_name',
  phone: 'contact_number',
  city: 'city',
  dob: 'date_of_birth',
};

/**
 * Only what the trader actually changed — PATCH takes a partial record, so an
 * untouched field is left out rather than sent back as it came.
 *
 * `email` is read-only, so it can never appear here.
 */
const toPatch = (form, seed, details) => {
  const body = {};

  Object.entries(PATCHABLE).forEach(([key, field]) => {
    if (form[key] !== seed[key]) body[field] = form[key].trim();
  });
  if (form.country !== seed.country) body.country = countryName(form.country);
  if (form.phoneCode !== seed.phoneCode) {
    body.country_code = Number(form.phoneCode.replace(/\D/g, ''));
  }

  const hasOtherChanges = Object.keys(body).length > 0;
  if (hasOtherChanges) {
    if (!body.country && !details?.country && form.country) {
      body.country = countryName(form.country);
    }
    if (!body.country_code && !details?.country_code && form.phoneCode) {
      body.country_code = Number(form.phoneCode.replace(/\D/g, ''));
    }
  }

  return body;
};

const SAVE_FAILED = 'Please try again.';

/** The record's fields this form can edit while they are still empty. */
const LOCKABLE_FIELDS = [
  'first_name',
  'last_name',
  'contact_number',
  'country',
  'city',
  'date_of_birth',
];

/**
 * A refusal is either `{ detail: "…" }` or per-field `{ first_name: ["…"] }`,
 * so the toast can say what was actually wrong rather than that something was.
 */
const saveErrorMessage = (error) => {
  const body = error?.data;
  if (typeof body === 'string') return body;

  const first = body?.detail ?? Object.values(body ?? {})[0];
  const message = Array.isArray(first) ? first[0] : first;
  return typeof message === 'string' && message ? message : SAVE_FAILED;
};

/**
 * The daily loss alert email, on or off — saved the moment it is flipped,
 * apart from the details form and its Save button.
 */
function DailyLossAlertSetting() {
  const { data, isLoading, isError } = useGetDailyLossAlertEmailQuery();
  const [update, { isLoading: isSaving }] = useUpdateDailyLossAlertEmailMutation();
  const on = Boolean(data?.daily_loss_alert_email);

  const toggle = async () => {
    try {
      await update(!on).unwrap();
    } catch {
      toast.error(t('Setting not saved'), t('Something went wrong. Please try again.'));
    }
  };

  return (
    <div className="profile-alert">
      <div className="profile-alert__copy">
        <span className="profile-alert__title">{t('Daily loss alert email')}</span>
        <span className="profile-note">
          {isError
            ? t('We could not load this setting.')
            : t('Get an email about your accounts’ daily loss limit.')}
        </span>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={t('Daily loss alert email')}
        className={`profile-switch${on ? ' is-on' : ''}`}
        onClick={toggle}
        disabled={isLoading || isError || isSaving}
      />
    </div>
  );
}

export default function ProfilePersonal() {
  const user = useSelector(selectUser);
  const { data, isLoading, isError, refetch } = useGetProfileDetailsQuery();
  const [updateProfile, { isLoading: isSaving }] = useUpdateProfileDetailsMutation();

  const seed = useMemo(() => toForm(data, user), [data, user]);
  const [form, setForm] = useState(seed);
  const [errors, setErrors] = useState({});
  const [failed, setFailed] = useState(null);

  // Only when the record itself changes, so a refetch mid-edit can't reach in
  // and overwrite what the trader has typed.
  useEffect(() => {
    setForm(seed);
    setErrors({});
  }, [seed]);

  const edit = ({ target }) => {
    setForm((prev) => ({ ...prev, [target.name]: target.value }));
    setErrors((prev) => ({ ...prev, [target.name]: undefined }));
    setFailed(null);
  };

  /** The contact number takes digits and nothing else — see `digitsOnly`. */
  const editPhone = ({ target }) => {
    setForm((prev) => ({ ...prev, phone: digitsOnly(target.value) }));
    setErrors((prev) => ({ ...prev, phone: undefined }));
    setFailed(null);
  };

  /**
   * Fields the profile record already answers from the backend are locked;
   * whatever the record lacks (empty/null) stays editable so the user can add and save it.
   */
  const isLocked = (field) => {
    if (!data) return false;
    const val = data[field];
    if (val === null || val === undefined) return false;
    const str = String(val).trim();
    return str !== '' && str !== '0';
  };

  /**
   * Every editable field the record holds. Once all of them are set, nothing on
   * the page can change without support (TI review N56), so Save / Discard give
   * way to a note saying so.
   */
  const allLocked = Boolean(data) && LOCKABLE_FIELDS.every(isLocked);

  const patch = useMemo(() => toPatch(form, seed, data), [form, seed, data]);
  const dirty = Object.keys(patch).length > 0;

  const discard = () => {
    setForm(seed);
    setErrors({});
    setFailed(null);
  };

  const save = async () => {
    try {
      profileSchema.validateSync(form, { abortEarly: false });
    } catch (error) {
      setErrors(
        Object.fromEntries((error.inner ?? []).map((issue) => [issue.path, issue.message])),
      );
      return;
    }
    setErrors({});
    setFailed(null);

    try {
      await updateProfile(patch).unwrap();
      toast.success(t('Profile updated'), t('Your details have been saved.'));
    } catch (error) {
      const message = saveErrorMessage(error);
      // The toast carries "Profile not saved" as its title; the inline note has
      // to say it itself, since it is read on its own.
      setFailed(t('We could not save your changes. {{message}}', { message: t(message) }));
      toast.error(t('Profile not saved'), t(message));
    }
  };

  if (isLoading) return <PersonalSkeleton />;

  return (
    <section className="profile-section profile-personal">
      <div className="profile-identity">
        <span className="profile-identity__avatar">
          {data?.photo ? (
            <img className="profile-identity__photo" src={data.photo} alt="" />
          ) : (
            initials(form.firstName, form.lastName)
          )}
        </span>
        <div>
          <div className="profile-identity__name">
            {form.firstName} {form.lastName}
          </div>
          <div className="profile-identity__email">{form.email}</div>
        </div>
      </div>

      {isError && (
        <div className="profile-personal__error">
          <span>{t('We could not load your profile details.')}</span>
          <button type="button" className="profile-personal__retry" onClick={refetch}>
            {t('Try again')}
          </button>
        </div>
      )}

      <div className="profile-fields">
        <Input
          label={t('First Name')}
          name="firstName"
          placeholder={t('Enter your first name')}
          value={form.firstName}
          onChange={edit}
          error={t(errors.firstName)}
          disabled={isLocked('first_name')}
        />
        <Input
          label={t('Last Name')}
          name="lastName"
          placeholder={t('Enter your last name')}
          value={form.lastName}
          onChange={edit}
          error={t(errors.lastName)}
          disabled={isLocked('last_name')}
        />
        <NumberInput
          label={t('Contact Number')}
          name="phone"
          placeholder={t('Enter your contact number')}
          value={form.phone}
          onChange={editPhone}
          error={t(errors.phone)}
          // The control stops at the same length the schema does, so the cap is
          // felt while typing instead of being reported after pressing Save.
          maxLength={PHONE_MAX}
          inputMode="numeric"
          autoComplete="tel-national"
          countryCode={form.phoneCode}
          onCountryCodeChange={(code) => setForm((prev) => ({ ...prev, phoneCode: code }))}
          // Locked once set, like the rest of the identity (TI review N56):
          // nothing here changes without support — the dial code with it.
          disabled={isLocked('contact_number')}
        />
        {/* The email is the sign-in identity, changed from Password → account
            security rather than here, so it is shown read-only. */}
        <Input
          label={t('Email')}
          type="email"
          name="email"
          value={form.email}
          onChange={edit}
          disabled
          autoComplete="email"
        />
        <Select
          label={t('Country')}
          name="country"
          value={form.country}
          onChange={edit}
          error={t(errors.country)}
          disabled={isLocked('country')}
        />
        <Input
          label={t('City')}
          name="city"
          placeholder={t('Enter your city')}
          value={form.city}
          onChange={edit}
          error={t(errors.city)}
          disabled={isLocked('city')}
        />
        <Input
          label={t('Date of birth')}
          type="date"
          name="dob"
          value={form.dob}
          onChange={edit}
          error={t(errors.dob)}
          disabled={isLocked('date_of_birth')}
          startPanel="year"
        />
      </div>

      <span className="profile-rule" />

      <DailyLossAlertSetting />

      <span className="profile-rule" />

      <div className="profile-actions">
        {allLocked ? (
          <span className="profile-note">
            {t('Your details are locked. To change them, please contact support.')}
          </span>
        ) : (
          <>
            {failed && <span className="profile-note profile-personal__failed">{failed}</span>}
            <Button variant="ghost" disabled={!dirty || isSaving} onClick={discard}>
              {t('Discard')}
            </Button>
            <Button variant="submit" disabled={!dirty || isSaving} onClick={save}>
              {isSaving ? t('Saving…') : t('Save changes')}
            </Button>
          </>
        )}
      </div>
    </section>
  );
}

/** The page's own layout, with stand-ins for the identity row and seven fields. */
function PersonalSkeleton() {
  return (
    <section className="profile-section profile-personal">
      <div className="profile-identity">
        <Skeleton w={42} h={42} pill />
        <div className="profile-personal__identity-lines">
          <Skeleton w={132} h={11} />
          <Skeleton w={180} h={9} />
        </div>
      </div>

      <div className="profile-fields">
        {[0, 1, 2, 3, 4, 5, 6].map((field) => (
          <div key={field} className="profile-personal__field-skeleton">
            <Skeleton w={78} h={9} />
            <Skeleton h={40} radius={8} />
          </div>
        ))}
      </div>
    </section>
  );
}
